// Discord Card Component
// Stunning Discord community showcase with user account linking

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { logger } from '../../../core';
import { DiscordUnlinkModal } from './';
import './DiscordCard.css';

export function DiscordCard({ user, viewMode = 'auto' }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [discordLinked, setDiscordLinked] = useState(false);
  const [discordUser, setDiscordUser] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLinkingDiscord, setIsLinkingDiscord] = useState(false);
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [isUnlinkingDiscord, setIsUnlinkingDiscord] = useState(false);

  const checkDiscordStatus = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await fetch('/api/account/discord/status', {
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success) {
        setDiscordLinked(data.isLinked || false);
        setDiscordUser(data.discordUser || null);
      }
    } catch (error) {
      logger.log({
        tags: "account|discord|error",
        color1: "red"
      }, "Failed to check Discord status:", error);
    }
  }, [user?.id]);

  const loadInitialDiscordCardData = useCallback(async () => {
    setIsInitialLoading(true);
    try {
      await checkDiscordStatus();
    } finally {
      setIsInitialLoading(false);
    }
  }, [checkDiscordStatus]);

  // Check Discord linking status and server info on component mount
  useEffect(() => {
    loadInitialDiscordCardData();
  }, [loadInitialDiscordCardData]);

  // Handle OAuth success/error messages
  useEffect(() => {
    const discordSuccess = searchParams.get('discord_success');
    const discordError = searchParams.get('discord_error');

    if (discordSuccess === 'linked') {
      logger.log({
        tags: "account|discord|success",
        color1: "green"
      }, "Discord account linked successfully");

      // Refresh Discord status
      checkDiscordStatus();

      // Clear URL parameters
      searchParams.delete('discord_success');
      setSearchParams(searchParams, { replace: true });
    }

    if (discordError) {
      logger.log({
        tags: "account|discord|error",
        color1: "red"
      }, "Discord OAuth error:", discordError);

      // Clear URL parameters
      searchParams.delete('discord_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, checkDiscordStatus]);

  const handleLinkDiscord = async () => {
    if (isLinkingDiscord) return;

    setIsLinkingDiscord(true);

    try {
      logger.log({
        tags: "account|discord|action",
        color1: "cyan"
      }, "Discord linking initiated");

      // Get OAuth URL from backend
      const response = await fetch('/api/discord/oauth', {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || 'Failed to initiate Discord OAuth');
      }

      if (data.oauthUrl) {
        // Redirect to Discord OAuth
        window.location.href = data.oauthUrl;
      } else {
        throw new Error('No OAuth URL received');
      }

    } catch (error) {
      logger.log({
        tags: "account|discord|error",
        color1: "red"
      }, "Failed to link Discord:", error);

      // Show error message to user
      alert(`Failed to connect to Discord: ${error.message}`);
    } finally {
      setIsLinkingDiscord(false);
    }
  };

  const handleUnlinkDiscord = () => {
    setIsUnlinkModalOpen(true);
  };

  const confirmUnlinkDiscord = async () => {
    setIsUnlinkingDiscord(true);

    try {
      logger.log({
        tags: "account|discord|action",
        color1: "cyan"
      }, "Discord unlinking initiated");

      const response = await fetch('/api/discord/link', {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || 'Failed to unlink Discord account');
      }

      // Update local state
      setDiscordLinked(false);
      setDiscordUser(null);

      logger.log({
        tags: "account|discord|success",
        color1: "green"
      }, "Discord account unlinked successfully");

    } catch (error) {
      logger.log({
        tags: "account|discord|error",
        color1: "red"
      }, "Failed to unlink Discord:", error);

      // Show error to user
      alert(`Failed to unlink Discord account: ${error.message}`);
    } finally {
      setIsUnlinkingDiscord(false);
    }
  };

  const resolvedViewMode = viewMode === 'auto'
    ? (discordLinked ? 'connected' : 'unlinked')
    : viewMode;

  return (
    <div className="account-card">
      <div className="card-header">
        <div className="card-title">Discord Community</div>
      </div>

      <div className="card-body">
        <div className={`discord-card__content discord-card__content--${resolvedViewMode}`}>
          {isInitialLoading ? (
            <div className="discord-card__initial-loading">
              <div className="discord-card__spinner" />
              <div className="discord-card__loading-text">Loading Discord status...</div>
              <div className="discord-card__loading-skeleton"></div>
              <div className="discord-card__loading-skeleton discord-card__loading-skeleton--small"></div>
            </div>
          ) : (
            <>
          {resolvedViewMode === 'connected' ? (
            <div className="discord-card__account-status">
              <div className="discord-card__status-header">
                <div className="discord-card__status-indicator">
                  <div className="discord-card__status-dot"></div>
                  <span>Account Connected</span>
                </div>
                <div className="discord-card__account-avatar">
                  {discordUser?.avatar ? (
                    <img
                      src={`https://cdn.discordapp.com/avatars/${discordUser.id}/${discordUser.avatar}.png`}
                      alt="Discord Avatar"
                      className="discord-card__avatar-image"
                    />
                  ) : (
                    <div className="discord-card__avatar-placeholder">
                      {discordUser?.username?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
              </div>

              <div className="discord-card__account-info">
                <div className="discord-card__username">
                  {discordUser?.username}{discordUser?.discriminator && discordUser.discriminator !== '0' ? `#${discordUser.discriminator}` : ''}
                </div>
                <div className="discord-card__account-description">
                  Your account is linked and ready to receive notifications.
                </div>
              </div>

              <div className="discord-card__account-actions">
                <button
                  onClick={handleUnlinkDiscord}
                  className="account-btn secondary small"
                >
                  Unlink Account
                </button>
              </div>
            </div>
          ) : (
            <div className="discord-card__connect-focus">
              <div className="discord-card__connect-header">
                <div className="discord-card__connect-title">Link your Discord account</div>
                <div className="discord-card__connect-subtitle">
                  Connect once to unlock announcements, support channels, and community perks.
                </div>
              </div>

              <div className="discord-card__benefits discord-card__benefits--compact">
                <div className="discord-card__benefit">
                  <span className="discord-card__benefit-icon">🔔</span>
                  <span>Get product updates and important notices</span>
                </div>
                <div className="discord-card__benefit">
                  <span className="discord-card__benefit-icon">🤝</span>
                  <span>Join members-only community discussions</span>
                </div>
              </div>

              <div className="discord-card__account-actions discord-card__account-actions--focus">
                <button
                  onClick={handleLinkDiscord}
                  disabled={isLinkingDiscord}
                  className="account-btn primary"
                >
                  {isLinkingDiscord ? '🔗 Connecting...' : '🔗 Link Discord Account'}
                </button>
              </div>
            </div>
          )}
            </>
          )}
        </div>
      </div>

      <DiscordUnlinkModal
        isOpen={isUnlinkModalOpen}
        onClose={() => setIsUnlinkModalOpen(false)}
        onConfirm={confirmUnlinkDiscord}
        discordUser={discordUser}
        isUnlinking={isUnlinkingDiscord}
      />
    </div>
  );
}